import { ReactNode } from "react";

export type Direction = "up" | "right" | "down" | "left";

export type FlowComponentType =
  | "pipe"
  | "bend"
  | "triClamp"
  | "ballValve"
  | "diaphragmValve"
  | "tPipe"
  | "heatExchanger"
  | "pump"
  | "tank";

export type FlowComponentCategory =
  | "Pipes & Fittings"
  | "Connections"
  | "Valves"
  | "Equipment";

export type FlowComponentVisualKind =
  | "pipe"
  | "bend"
  | "triClamp"
  | "ballValve"
  | "diaphragmValve"
  | "tPipe"
  | "heatExchanger"
  | "pump"
  | "tank";

export interface FlowComponentFieldDefinition {
  key: string;
  label: string;
  inputType?: "text" | "color" | "select";
  options?: { label: string; value: string }[];
}

export interface FlowComponentRenderContext {
  node: FlowNode;
  componentColor: string;
  arrowColor: string;
  pulseColor: string;
  startPoint: { x: number; y: number };
  endPoint: { x: number; y: number };
  pipeArrowDirection: Direction;
  isHorizontal: boolean;
  isHeatExchangerVertical: boolean;
  isBallValveClosed: boolean;
}

export interface FlowComponentDefinition {
  type: FlowComponentType;
  title: string;
  category: FlowComponentCategory;
  visualKind: FlowComponentVisualKind;
  defaultColor: string;
  previewFlow: {
    from: Direction;
    to: Direction;
  };
  ports: Direction[];
  incomingDirections: Direction[];
  outgoingDirections: Direction[];
  defaults: Record<string, string>;
  fields: FlowComponentFieldDefinition[];
  renderGraphic: (context: FlowComponentRenderContext) => ReactNode;
  renderAnimatedFlow?: (context: FlowComponentRenderContext) => ReactNode;
}

export interface FlowNode {
  id: string;
  type: FlowComponentType;
  x: number;
  y: number;
  locked: boolean;
  startFlow: boolean;
  label: string;
  color: string;
  flow: {
    from: Direction;
    to: Direction;
  };
  values: Record<string, string>;
}

export interface SuperCalculator extends Calculator {
  // add content here
}

export enum AppData {
  CALCULATOR = "calculator",
  ADVANCED_CALCULATOR = "advanced-calculator",
  CONVERTER = "converter",
  RECEIPT = "receipt",
  FLOW = "flow",
}

export interface FlowWorkspaceData {
  type: AppData.FLOW;
  id: string;
  nodes: FlowNode[];
  pan: {
    x: number;
    y: number;
  };
  zoom: number;
}

export interface GraphVariable {
  key: string;
  label: string;
  baseUnit: string;
  selectedUnit: string;
  units: BaseQuantity["units"];
}

export interface GraphLegend {
  id: string;
  xKey: string;
  yKey: string;
  color: string;
  borderDash?: number[];
}

export interface GraphControls {
  xMin: number;
  xMax: number;
  yMin: number | null;
  yMax: number | null;
  xDelta: number;
  trackingIncrement: number;
  xDecimals: number;
}

export interface GraphSavedPointValue {
  key: string;
  label: string;
  unit: string;
  value: number;
}

export interface GraphSavedPoint {
  id: string;
  xValue: number;
  xLabel: string;
  xUnit: string;
  values: GraphSavedPointValue[];
}

export interface GraphConfiguration {
  contextKey: string;
  activeTab: "legends" | "controls";
  isPointsPanelOpen: boolean;
  legends: GraphLegend[];
  controls: GraphControls;
  savedPoints: GraphSavedPoint[];
}

export interface Converter
  extends Omit<Calculator, "branch" | "calculate" | "difficulty"> {}

export type CalculatorBranch =
  | "Physics"
  | "Biology"
  | "Chemistry"
  | "Geometry"
  | "Mathematics"
  | "Thermal"
  | "Fluids"
  | "Electric";

export type CalculatorDifficulty = "simple" | "intermediate" | "complex";

export interface Calculator {
  type: AppData.CALCULATOR | AppData.CONVERTER;
  name: string;
  updatedAt: string;
  branch: CalculatorBranch;
  difficulty: CalculatorDifficulty;
  graphConfiguration?: GraphConfiguration;
  inputs: CalcMode[];
  calculate: {
    name: string;
    equations: {
      target: string;
      /* All variables must be surrounded with parenthesis! */
      expression: string;
    }[];
  }[];
}

export interface AdvancedCalculatorInput extends BaseQuantity {
  clearValue?: string;
  section?: string;
}

export interface AdvancedCalculatorOutput extends BaseQuantity {
  decimalsOffset?: number;
  section?: string;
}

export interface AdvancedCalculatorChartAxis {
  key: string;
  label: string;
  baseUnit: string;
  selectedUnit: string;
  selectedUnitFromInput?: string;
  units: BaseQuantity["units"];
}

export interface AdvancedCalculatorChartDataset
  extends AdvancedCalculatorChartAxis {
  axisId: string;
  borderColor: string;
  borderDash?: number[];
}

export interface AdvancedCalculatorChart {
  title: string;
  xAxis: AdvancedCalculatorChartAxis;
  datasets: AdvancedCalculatorChartDataset[];
}

export interface AdvancedCalculatorResult {
  outputs: Record<string, number>;
  series?: Record<string, number>[];
}

export interface AdvancedCalculator {
  type: AppData.ADVANCED_CALCULATOR;
  definitionId: string;
  name: string;
  updatedAt: string;
  branch: CalculatorBranch;
  difficulty: CalculatorDifficulty;
  graphConfiguration?: GraphConfiguration;
  equation?: string;
  figure?: string;
  inputs: AdvancedCalculatorInput[];
  outputs: AdvancedCalculatorOutput[];
  chart?: AdvancedCalculatorChart;
  calculate: (
    inputs: Record<string, number>
  ) => AdvancedCalculatorResult | null;
  validate?: (inputs: Record<string, number>) => string[];
}
// can take calcmode that is of
// bq length
// gets key word "pitch"
// get the datatype arr
// it will tap into arr.data[key-word]
export interface CalcMode {
  id: string;
  name: "standard" | string;
  baseQuantities: BaseQuantity[];
  equation?: string;
  figure?: any;
}

export interface DropdownQuantity extends BaseQuantity {}

export interface BaseQuantity {
  id: string;
  /* no space allowed, use underscore instead */
  /* No parenthesis allowed in name */
  name: string;
  type: string;
  value: any;
  symbol: string;
  baseUnit: string;
  selectedUnit: string;
  /* fromConst: from this unit to base unit and vice versa */
  /* 
      if fromConst or toConst conversion requires other operation than multiplication, 
      then wrap it in a string and set write the conversion value as "value"
      VALUE MUST BE WRAPPED IN PARENTHESIS!!!
  */
  units: {
    name: string;
    fromConst: number | string;
    toConst: number | string;
  }[];
  datavalues?: { label: string; value: string }[];
  toolTip?: string;
}

export interface Receipt {
  type: AppData.RECEIPT;
  id: any;
  label: string;
  output: {
    name: string;
    value: number | string;
    unit?: string;
  }[];
  timestamp: number;
  mode: string;
}

export interface Notification {
  message?: string;
  type?: "SUCCESS" | "ERROR" | "WARNING" | "INFORMATION";
}

export interface Mail {
  name: string;
  email: string;
  message: string;
  mailSent: boolean;
}

export interface TutorialStep {
  title: string;
  text: string; // Descriptive text
  isCompleted?: boolean; // if task is completed
  hideComponent?: boolean; // if hide react component
  helpText?: string; // Hidden help text for user to hover over, or focus
  nextButtonText?: string; // change text of next button
  prevButtonText?: string; // change text of previous button
  successText?: string; // When user clears the listener task
  focusQuery?: string; // what element to focus on
  eventListenerType?: keyof WindowEventMap;
  task?: (e?: any) => boolean; // Task to complete step and advance
}

export interface PopupToggle {
  showTutorial?: boolean;
  showSearchcalc?: boolean;
  showReceipt?: boolean;
  showImportCalcs?: boolean;
}
